import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { DialogData } from '../../../../shared/models/dialog-data.model';
import { FORM_ERROR_MESSAGES } from '../../../dashboard/dialogs/add-website-dialog/add-website-dialog.const';
import { MatButtonModule } from '@angular/material/button';
import { InputField } from '../../../../shared/components/input-field/input-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { EmailValidators } from '../../../../shared/components/validators/email.validators';
import { PersonValidators } from '../../../../shared/components/validators/person.validators';
import { SizeInputComponent } from '../../../../shared/components/size-input/size-input.component';
import { MatRadioModule } from '@angular/material/radio';
import { PasswordValidators } from '../../../../shared/components/validators/password.validators';
import {
  MailboxUserCreate,
  MailboxUserUpdate,
  User,
} from '../../../../shared/models/mailbox.models';
import { Server } from '../../../../shared/models/server.models';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  map,
  shareReplay,
  combineLatest,
  withLatestFrom,
  of,
  catchError,
} from 'rxjs';
import { WebsitesService } from '../../../../core/services/websites.service';
import { AsyncPipe } from '@angular/common';
import { MailboxService } from '../../../../core/services/mailbox.service';

@Component({
  selector: 'app-add-mailbox-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    InputField,
    MatCheckboxModule,
    SizeInputComponent,
    MatRadioModule,
    FormsModule,
    AsyncPipe,
  ],
  templateUrl: './add-mailbox-dialog.component.html',
  styleUrl: './add-mailbox-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddMailboxDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<AddMailboxDialogComponent>);

  readonly data = inject<
    DialogData & {
      mailboxId?: number;
      systemUserId?: number;
      domain?: string;
      user?: User;
      servers: Server[];
    }
  >(MAT_DIALOG_DATA);

  private fb = inject(FormBuilder);

  private websitesService = inject(WebsitesService);

  private mailboxService = inject(MailboxService);

  readonly errors = FORM_ERROR_MESSAGES;

  passwordRequiredValidator = PasswordValidators.required();

  form = this.fb.nonNullable.group({
    systemUserId: [0],
    mailboxId: [0],
    address: ['', [Validators.minLength(3)]],
    password: [
      '',
      [
        this.passwordRequiredValidator,
        PasswordValidators.complexity(),
        PasswordValidators.allowedCharacters(),
        PasswordValidators.minLength(),
        PasswordValidators.maxLength(),
      ],
    ],
    realName: ['', PersonValidators.realName()],
    aliases: [
      [] as string[],
      [EmailValidators.emailArray('Aliases must be valid email addresses')],
    ],
    forwards: [
      [] as string[],
      [EmailValidators.emailArray('Forwards must be valid email addresses')],
    ],
    localDelivery: [{ value: false, disabled: true }],
    quota: this.fb.nonNullable.group({
      blockSoft: [0],
      blockHard: [0],
      radio: ['2'], // Utility control
    }),
    serverId: [], // Utility control
    websiteId: [],
  });

  get isMailboxUnknown(): boolean {
    return !this.data.mailboxId && !this.data.user;
  }

  get servers(): { label: string; value: string }[] {
    return this.data.servers.map((s) => ({ label: s.hostname, value: s._id }));
  }

  emailSuffix = signal(this.data.domain);

  serverSearchText = signal<string>('');

  filteredServers = computed(() =>
    this.servers.filter((s) =>
      this.serverSearchText() ? s.label.includes(this.serverSearchText()) : true,
    ),
  );

  websiteSearchText = signal<string>('');

  websiteSearchText$ = toObservable(this.websiteSearchText);

  websites$ = combineLatest({
    domain: this.websiteSearchText$.pipe(debounceTime(500), distinctUntilChanged()),
    server: this.form.controls.serverId.valueChanges,
  }).pipe(
    switchMap(({ domain, server }) =>
      this.websitesService.searchWebsites({ domainSearch: domain, serverSearch: server }),
    ),
    map(({ Websites }) => Websites),
    shareReplay(1),
  );

  websiteOptions$ = this.websites$.pipe(
    map((websites) => websites.map((w) => ({ label: w.domain, value: String(w.id) }))),
  );

  ngOnInit(): void {
    const controls = this.form.controls;
    if (this.data.mailboxId && this.data.systemUserId) {
      controls.mailboxId.setValue(this.data.mailboxId);
      controls.systemUserId.setValue(this.data.systemUserId);
    } else if (this.data.user) {
      controls.aliases.setValue(this.data.user.aliases);
      controls.forwards.setValue(this.data.user.forwards);
      controls.realName.setValue(this.data.user.realName);

      let { blockHard } = this.data.user;
      if (blockHard < 0) {
        blockHard = 0;
      } else {
        controls.quota.controls.radio.setValue('1');
      }
      controls.quota.controls.blockHard.setValue(blockHard);
      controls.quota.controls.blockSoft.setValue(blockHard);

      controls.password.removeValidators(this.passwordRequiredValidator);
      this.form.updateValueAndValidity();
    }

    this.listenForChanges();
  }

  listenForChanges(): void {
    this.form.controls.websiteId.valueChanges
      .pipe(
        withLatestFrom(this.websites$),
        switchMap(([id, websites]) => {
          const selectedWebsite = websites.find((w) => w.id === +id);
          return selectedWebsite
            ? combineLatest({
                website: of(selectedWebsite),
                mailbox: this.mailboxService.getWebsiteMailbox(selectedWebsite.id).pipe(
                  catchError(() => {
                    this.form.controls.websiteId.setErrors({ noService: true });
                    return of(null);
                  }),
                ),
              })
            : of({ website: null, mailbox: null });
        }),
      )
      .subscribe(({ website, mailbox }) => {
        if (website) {
          this.form.controls.systemUserId.setValue(website.details.unixUserId);
          this.emailSuffix.set(website.domain);
        }
        if (mailbox) {
          this.form.controls.mailboxId.setValue(mailbox.mailbox.id);
        }
      });

    this.form.controls.forwards.valueChanges.subscribe((forwards) => {
      if (forwards.length > 0) this.form.controls.localDelivery.enable();
      else this.form.controls.localDelivery.disable();
    });
  }

  private createCreateData(): MailboxUserCreate {
    const {
      mailboxId,
      systemUserId,
      address,
      password,
      realName,
      aliases,
      forwards,
      quota,
      localDelivery,
    } = this.form.getRawValue();

    return {
      mailboxId,
      systemUserId,
      address: `${address}@${this.emailSuffix()}`,
      password,
      realName,
      aliases,
      forwards,
      localDelivery,
      ...(quota?.blockSoft
        ? {
            quota: {
              blockHard: quota.blockSoft,
              blockSoft: quota.blockSoft,
            },
          }
        : {}),
    };
  }

  createUpdateData(): MailboxUserUpdate {
    const { aliases, forwards, quota, realName, password, localDelivery } = this.form.getRawValue();

    return {
      aliases,
      forwards,
      localDelivery,
      quota:
        quota.radio === '1'
          ? { blockHard: quota.blockSoft, blockSoft: quota.blockSoft }
          : undefined,
      realName,
      ...(password ? { password } : {}),
    };
  }

  submit(): void {
    if (this.form.invalid) return;

    const data = this.data.user ? this.createUpdateData() : this.createCreateData();

    this.dialogRef.close(data);
  }

  close(): void {
    this.dialogRef.close();
  }
}
